// Scenario screen — connects to the Colyseus room, renders MonitorPanel + action toolbar.
// MVP: shows the live state. Action toolbar lets the learner pulse CPR, shock, drug.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Client, Room } from 'colyseus.js';
import { api, type Me } from '../lib/api';
import MonitorPanel, { type MonitorState } from '../components/Monitor/MonitorPanel';
import NEWS2Panel from '../components/Monitor/NEWS2Panel';
import MonitorAsText from '../components/Monitor/MonitorAsText';
import PatientAvatar from '../components/PatientCanvas/PatientAvatar';
import AlgorithmReference from '../components/AlgorithmReference';
import SocraticPromptModal, { type SocraticPrompt } from '../components/SocraticPromptModal';
import DrugCart from '../components/Drawers/DrugCart';
import DefibPanel from '../components/Drawers/DefibPanel';
import AirwayKit from '../components/Drawers/AirwayKit';
import DiagnosticsPanel from '../components/Drawers/DiagnosticsPanel';
import { unlockAudio, setMuted, isMuted } from '../lib/audio';
import { updateMonitorAudio, announceAction, announceEvent, defibCharge, defibDischarge } from '../lib/audio-controller';
import type { Rhythm } from '../lib/waveforms';

interface ActionLogEntry { id: number; t: number; label: string; flashKey?: string; }
interface TeachEntry { id: number; severity: 'hint' | 'warn' | 'critical'; text: string; t_ms: number; }

const FOUR_HS_4TS: Array<{ key: string; label: string }> = [
  { key: 'hypoxia', label: 'Hypoxia' },
  { key: 'hypovolaemia', label: 'Hypovolaemia' },
  { key: 'hyperkalaemia', label: 'Hyper/hypoK+ & metabolic' },
  { key: 'hypothermia', label: 'Hypothermia' },
  { key: 'tension_pneumothorax', label: 'Tension PTX' },
  { key: 'tamponade', label: 'Tamponade' },
  { key: 'toxins', label: 'Toxins' },
  { key: 'thrombosis', label: 'Thrombosis (PE/MI)' },
];

const ACTION_LABEL: Record<string, string> = {
  cpr_pulse: 'CPR pulse',
  start_compressions: 'Start CPR',
  stop_compressions: 'Stop CPR',
  rhythm_check: 'Rhythm check',
  pulse_check_carotid: 'Pulse check (carotid)',
  charge_defib: 'Charge defibrillator',
  shock: 'Shock delivered',
  drug_admin: 'Drug administered',
  insert_igel: 'i-gel inserted',
  bvm_ventilation: 'BVM breath',
  declare_4hs_4ts: 'Declared 4Hs/4Ts',
  call_for_help: 'Called for help',
  team_callout: 'Closed-loop callout',
  declare_differential: 'Declared differential',
};

const ROOM_NAME = 'als';

function colyseusEndpoint(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // In prod, nginx proxies /ws/ to the rooms server. In dev, Vite proxies the same.
  return `${proto}//${window.location.host}/ws`;
}

interface RhythmAware { rhythm?: string; }

function isRhythm(v: string | undefined): v is Rhythm {
  return !!v && ['sinus','sinus_tach','brady','af','svt','vt_pulse','vt_pulseless','vf','torsades','asystole','pea_narrow','pea_broad','chb'].includes(v);
}

export default function Scenario({ me }: { me: Me }) {
  const { id: scenarioId } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === '1';
  const [scenario, setScenario] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(true);
  const roomRef = useRef<Room | null>(null);
  const clientRef = useRef<Client | null>(null);
  const [state, setState] = useState<MonitorState>({
    rhythm: 'sinus',
    hr: 0,
    sbp: 0,
    dbp: 0,
    spo2: 0,
    rr: 0,
    etco2_mmhg: 0,
    temp_c: 37.0,
    sim_t_ms: 0,
    cpr_active: false,
    cpr_quality_pct: 0,
    cpr_rate_cpm: 0,
    intubated: false,
  });
  const [lastShockAt, setLastShockAt] = useState<number | undefined>(undefined);
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  const [teachFeed, setTeachFeed] = useState<TeachEntry[]>([]);
  const [considered, setConsidered] = useState<Set<string>>(new Set());
  const [currentPhase, setCurrentPhase] = useState<string>('pre_arrest');
  const [airway, setAirway] = useState<string>('natural');
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const [algorithmOpen, setAlgorithmOpen] = useState(false);
  const [socraticPrompt, setSocraticPrompt] = useState<SocraticPrompt | null>(null);
  const [paused, setPaused] = useState(false);
  const [drugsOpen, setDrugsOpen] = useState(false);
  const [defibOpen, setDefibOpen] = useState(false);
  const [airwayOpen, setAirwayOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [muted, setMutedState] = useState(false);
  const teachSeenRef = useRef<Set<number>>(new Set());
  const actionIdRef = useRef(0);

  // Load scenario metadata
  useEffect(() => {
    if (!scenarioId) return;
    api.scenarioById(scenarioId)
      .then((r) => setScenario(r.scenario))
      .catch((e) => setErr(e.message || 'Failed to load scenario'));
  }, [scenarioId]);

  // Connect to Colyseus room
  useEffect(() => {
    if (!scenario) return;
    const client = new Client(colyseusEndpoint());
    clientRef.current = client;
    (async () => {
      try {
        const initState = scenario.initial_state_json || {};
        const room = await client.joinOrCreate(ROOM_NAME, {
          scenarioId: scenario.id,
          region: scenario.region,
          difficulty: scenario.difficulty,
          initial_rhythm: (initState.rhythm as Rhythm) || 'vf',
          initial_phase: initState.phase || (initState.rhythm && ['vf','vt_pulseless','asystole','pea_narrow','pea_broad','torsades'].includes(initState.rhythm) ? 'arrest' : 'pre_arrest'),
          hidden_diagnosis: scenario.hidden_diagnosis,
          reversible_required: initState.reversible_required || [],
          deterioration: initState.deterioration,
          user_id: me.id,
          display_name: me.display_name,
          role: 'leader',
        });
        roomRef.current = room;
        room.onStateChange((s: any) => {
          const v = s.patient?.vitals;
          if (!v) return;
          setState((prev) => ({
            ...prev,
            rhythm: isRhythm(v.rhythm) ? v.rhythm : prev.rhythm,
            hr: v.hr ?? 0,
            sbp: v.sbp ?? 0,
            dbp: v.dbp ?? 0,
            spo2: v.spo2 ?? 0,
            rr: v.rr ?? 0,
            etco2_mmhg: (v.etco2_x10 ?? 0) / 10,
            temp_c: (v.temp_c_x10 ?? 370) / 10,
            sim_t_ms: s.patient.sim_t_ms ?? 0,
            cpr_active: !!s.patient.cpr_active,
            cpr_quality_pct: s.patient.cpr_quality_pct ?? 0,
            cpr_rate_cpm: s.patient.cpr_rate_cpm ?? 0,
            intubated: ['igel', 'lma', 'ett'].includes(s.patient.airway || ''),
          }));
          // Pick up reversible-cause ticks from the synced array
          const ar = s.patient?.reversible_causes_considered;
          if (ar && typeof ar.length === 'number') {
            const next = new Set<string>();
            for (let i = 0; i < ar.length; i++) next.add(String(ar[i]));
            setConsidered(next);
          }
          // Phase + airway for the indicator strip
          if (s.patient?.current_phase) setCurrentPhase(String(s.patient.current_phase));
          if (s.patient?.airway) setAirway(String(s.patient.airway));
          // Teach messages: append any new ones we haven't seen
          const tm = s.recent_teach;
          if (tm && typeof tm.length === 'number') {
            const fresh: TeachEntry[] = [];
            for (let i = 0; i < tm.length; i++) {
              const m = tm[i];
              if (!m || teachSeenRef.current.has(m.id)) continue;
              teachSeenRef.current.add(m.id);
              const sev: TeachEntry['severity'] = (m.severity === 'critical' || m.severity === 'warn') ? m.severity : 'hint';
              fresh.push({ id: m.id, severity: sev, text: m.text_md, t_ms: m.t_ms });
            }
            if (fresh.length) setTeachFeed((prev) => [...prev, ...fresh].slice(-10));
          }
        });
        room.onMessage('EVENT', (msg: any) => {
          if (msg?.kind === 'rosc') {
            const e: TeachEntry = { id: Date.now(), severity: 'hint', text: `ROSC at ${(msg.t_ms / 1000).toFixed(0)}s`, t_ms: msg.t_ms };
            setTeachFeed((prev) => [...prev, e].slice(-10));
            announceEvent('rosc');
          }
          if (msg?.kind === 'rhythm_change') announceEvent('rhythm_change');
          if (msg?.kind === 'invalid_action') {
            console.warn('invalid action:', msg.detail);
          }
          if (msg?.kind === 'run_finalised' && msg.run_id) {
            // Server has finalised: navigate to debrief
            nav(`/debrief/${msg.run_id}`);
          }
        });
        room.onMessage('RUN_INFO', (msg: any) => {
          if (msg?.run_id) setRunId(msg.run_id);
        });
        room.onMessage('TEACH_PROMPT', (msg: any) => {
          if (!msg) return;
          setSocraticPrompt(msg as SocraticPrompt);
          // Append to the teach feed too, so it's there in the debrief / after dismissing
          const e: TeachEntry = {
            id: Date.now(),
            severity: 'critical',
            text: msg.lead_md,
            t_ms: msg.t_ms,
          };
          setTeachFeed((prev) => [...prev, e].slice(-10));
        });
        setConnecting(false);
      } catch (e: any) {
        setErr(`Failed to join room: ${e.message || e}`);
        setConnecting(false);
      }
    })();
    return () => {
      roomRef.current?.leave().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.id]);

  function send(action_type: string, payload?: any) {
    roomRef.current?.send('ACTION', { action_type, payload });
    // Unlock audio on first user gesture
    void unlockAudio();
    if (action_type === 'shock') {
      setLastShockAt(state.sim_t_ms);
      defibDischarge();
    }
    if (action_type === 'charge_defib') {
      defibCharge(payload?.joules || 200);
    }
    announceAction(action_type, payload?.drug, payload?.dose_mg);
    // Local log + button flash. cpr_pulse fires up to 120/min; we throttle its
    // log entries to one per 2 seconds so the feed doesn't drown.
    const now = Date.now();
    actionIdRef.current += 1;
    setActionLog((prev) => {
      const label = ACTION_LABEL[action_type] ?? action_type;
      if (action_type === 'cpr_pulse' && prev.length > 0 && prev[0]!.label === 'CPR pulse' && now - prev[0]!.t < 2000) {
        // collapse rapid CPR pulses
        return prev;
      }
      const detail = action_type === 'drug_admin' && payload?.drug
        ? `${label}: ${payload.drug} ${payload.dose_mg}mg ${payload.route}`
        : label;
      return [{ id: actionIdRef.current, t: now, label: detail, flashKey: action_type }, ...prev].slice(0, 20);
    });
    setFlashKey(action_type);
    setTimeout(() => setFlashKey((k) => (k === action_type ? null : k)), 350);
  }

  // Keyboard shortcuts: Space=CPR, D=shock, A=adrenaline (capital A), Shift+A=algorithm overlay,
  // P=pause (lenient only), Esc=close overlays
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      // Don't intercept when typing in inputs or modal is open and accepts text
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (ev.key === 'Escape') {
        if (algorithmOpen) setAlgorithmOpen(false);
        return;
      }
      if (ev.code === 'Space' && !ev.repeat && !socraticPrompt) {
        ev.preventDefault();
        send('cpr_pulse');
      } else if (ev.shiftKey && ev.key.toLowerCase() === 'a') {
        ev.preventDefault();
        setAlgorithmOpen(true);
      } else if (ev.key.toLowerCase() === 'd' && !socraticPrompt) {
        send('shock');
      } else if (ev.key.toLowerCase() === 'a' && !socraticPrompt) {
        send('drug_admin', { drug: 'adrenaline', dose_mg: 1, route: 'IV', indication: 'cardiac_arrest' });
      } else if (ev.key.toLowerCase() === 'p' && scenario?.difficulty === 'familiarisation') {
        ev.preventDefault();
        roomRef.current?.send('ADMIN_OVERRIDE', { kind: paused ? 'resume' : 'pause' });
        setPaused(!paused);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithmOpen, socraticPrompt, paused, scenario?.difficulty]);

  // Drive monitor audio from vitals
  useEffect(() => {
    updateMonitorAudio(state.rhythm, state.hr, state.spo2, currentPhase);
  }, [state.rhythm, state.hr, state.spo2, currentPhase]);

  // Stop audio on unmount
  useEffect(() => {
    return () => {
      updateMonitorAudio('sinus', 0, 0, 'terminated');
    };
  }, []);

  function toggleMute() {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  }

  if (err) return <div className="max-w-4xl mx-auto p-8 text-alarm-red">{err}</div>;
  if (!scenario || connecting) return <div className="max-w-4xl mx-auto p-8 text-mono-white/60">Connecting to scenario…</div>;

  const buttonClass = (key: string, primary = false) => {
    const base = primary ? 'btn-primary' : 'btn-ghost';
    return flashKey === key ? `${base} btn-flash` : base;
  };

  const phaseColor = currentPhase === 'rosc' || currentPhase === 'post_rosc' ? 'bg-mono-green/20 text-mono-green' :
                     currentPhase === 'arrest' ? 'bg-alarm-red/20 text-alarm-red' :
                     currentPhase === 'terminated' ? 'bg-ink-800 text-mono-white/50' :
                     'bg-ink-800 text-mono-white/70';

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {isDemo && (
        <div className="mb-4 p-3 rounded bg-brand-600/15 border-l-4 border-brand-600 flex justify-between items-center flex-wrap gap-2">
          <div className="text-sm">
            <span className="font-semibold text-brand-600">Demo mode</span>
            <span className="text-mono-white/70 ml-2">— playing as <span className="text-mono-white">{me.display_name}</span>. Sign up to track all your runs.</span>
          </div>
          <a href="/login" className="btn-primary text-xs">Sign up to save runs</a>
        </div>
      )}
      <AlgorithmReference open={algorithmOpen} onClose={() => setAlgorithmOpen(false)} />
      <SocraticPromptModal
        prompt={socraticPrompt}
        onDismiss={() => setSocraticPrompt(null)}
        onComplete={() => setSocraticPrompt(null)}
      />
      <DrugCart
        open={drugsOpen}
        onClose={() => setDrugsOpen(false)}
        onAdmin={(drug, dose_mg, route, indication) => send('drug_admin', { drug, dose_mg, route, indication })}
      />
      <DefibPanel
        open={defibOpen}
        onClose={() => setDefibOpen(false)}
        onChargeDefib={(joules) => send('charge_defib', { joules })}
        onShock={() => send('shock', { joules: 200 })}
        onSyncCardiovert={(joules) => send('sync_cardiovert', { joules })}
        onPaceTranscutaneous={(rate_bpm, mA) => send('pace_transcutaneous', { rate_bpm, mA })}
      />
      <AirwayKit
        open={airwayOpen}
        onClose={() => setAirwayOpen(false)}
        currentAirway={airway}
        onAction={send}
      />
      <DiagnosticsPanel
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        onAction={send}
      />
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <h1 className="font-semibold">{scenario.title}</h1>
            <p className="text-xs text-mono-white/50 mt-1">Region {scenario.region} · {scenario.difficulty} · T+{(state.sim_t_ms / 1000).toFixed(0)}s</p>
          </div>
          <span className={`chip ${phaseColor} uppercase tracking-wider`}>{currentPhase.replace('_', ' ')}</span>
          {airway !== 'natural' && <span className="chip bg-ink-800 text-mono-cyan uppercase">Airway: {airway}</span>}
          {paused && <span className="chip bg-alarm-amber/20 text-alarm-amber uppercase tracking-wider">Paused</span>}
          <button
            onClick={() => setAlgorithmOpen(true)}
            className="chip bg-ink-800 hover:bg-ink-900 text-brand-600 uppercase tracking-wider cursor-pointer"
            title="ALS algorithm reference (Shift+A)"
          >
            📋 Algorithm (Shift+A)
          </button>
          <button
            onClick={toggleMute}
            className="chip bg-ink-800 hover:bg-ink-900 text-mono-white uppercase tracking-wider cursor-pointer"
            title="Toggle audio"
          >
            {muted ? '🔇 Muted' : '🔊 Audio'}
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              if (ending || !runId) return;
              if (!window.confirm('End this run now? You\'ll go to the debrief.')) return;
              setEnding(true);
              const outcome = currentPhase === 'rosc' || currentPhase === 'post_rosc' ? 'ROSC' : 'aborted';
              roomRef.current?.send('END_RUN', { outcome });
              // navigation happens when server replies with run_finalised; fall back after 4s
              setTimeout(() => { if (runId) nav(`/debrief/${runId}`); }, 4000);
            }}
            disabled={ending || !runId}
            className="btn-primary text-sm"
          >
            {currentPhase === 'rosc' || currentPhase === 'post_rosc' ? 'End run · ROSC achieved' : 'End run (terminate)'}
          </button>
          <button onClick={() => nav('/dashboard')} className="btn-ghost text-sm">Quit (counts as termination)</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Left/centre: avatar + action toolbar + considered + feed */}
        <div className="lg:col-span-3 space-y-4">
          <PatientAvatar
            rhythm={state.rhythm}
            hr={state.hr}
            spo2={state.spo2}
            cpr_active={state.cpr_active}
            cpr_quality_pct={state.cpr_quality_pct}
            airway={airway}
            current_phase={currentPhase}
            intubated={state.intubated}
          />


          <div className="panel p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-mono-white/60 text-xs uppercase tracking-widest">Toolkit</div>
              <div className="text-mono-white/40 text-xs">CPR quality {state.cpr_quality_pct}%</div>
            </div>

            {/* Category buttons: open drawers */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              <button onClick={() => setDrugsOpen(true)} className="btn-ghost flex flex-col items-center py-3">
                <span className="text-xs uppercase tracking-widest text-mono-white/40">Drugs</span>
                <span className="font-medium mt-1">💊 Cart</span>
              </button>
              <button onClick={() => setDefibOpen(true)} className="btn-ghost flex flex-col items-center py-3">
                <span className="text-xs uppercase tracking-widest text-mono-white/40">Defib</span>
                <span className="font-medium mt-1">⚡ Panel</span>
              </button>
              <button onClick={() => setAirwayOpen(true)} className="btn-ghost flex flex-col items-center py-3">
                <span className="text-xs uppercase tracking-widest text-mono-white/40">Airway</span>
                <span className="font-medium mt-1">🫁 Kit</span>
              </button>
              <button onClick={() => setDiagOpen(true)} className="btn-ghost flex flex-col items-center py-3">
                <span className="text-xs uppercase tracking-widest text-mono-white/40">Diag</span>
                <span className="font-medium mt-1">🔬 Tests</span>
              </button>
            </div>

            {/* Quick actions for muscle memory */}
            <div className="text-mono-white/40 text-[10px] uppercase tracking-widest mb-2">Quick actions</div>
            <div className="grid grid-cols-3 gap-2" role="toolbar" aria-label="Quick action toolbar">
              <button onClick={() => send('start_compressions')} className={buttonClass('start_compressions')} aria-label="Start chest compressions">Start CPR</button>
              <button onClick={() => send('cpr_pulse')} className={buttonClass('cpr_pulse')} aria-label="Deliver one chest compression. Spacebar shortcut.">CPR pulse (Space)</button>
              <button onClick={() => send('stop_compressions')} className={buttonClass('stop_compressions')} aria-label="Stop chest compressions">Stop CPR</button>
              <button onClick={() => send('rhythm_check')} className={buttonClass('rhythm_check')} aria-label="Perform a rhythm check on the monitor">Rhythm check</button>
              <button onClick={() => send('pulse_check_carotid')} className={buttonClass('pulse_check_carotid')} aria-label="Palpate carotid pulse">Pulse check</button>
              <button onClick={() => send('shock')} className={buttonClass('shock', true)} aria-label="Deliver a defibrillation shock. D key shortcut.">Shock (D)</button>
              <button onClick={() => send('drug_admin', { drug: 'adrenaline', dose_mg: 1, route: 'IV', indication: 'cardiac_arrest_shockable' })} className={buttonClass('drug_admin')} aria-label="Administer adrenaline 1 milligram intravenous. A key shortcut.">Adrenaline 1mg (A)</button>
              <button onClick={() => send('bvm_ventilation')} className={buttonClass('bvm_ventilation')} aria-label="Deliver one bag-valve-mask breath">BVM breath</button>
              <button onClick={() => send('declare_4hs_4ts', { considered: FOUR_HS_4TS.map((c) => c.key) })} className={buttonClass('declare_4hs_4ts')} aria-label="Declare all four H's and four T's as considered">Declare all 4Hs/4Ts</button>
              <button onClick={() => send('call_for_help')} className={buttonClass('call_for_help')} aria-label="Call for help. In hospital UK: dial 2222.">Call for help (2222)</button>
              <button onClick={() => send('team_callout', { text: 'Continue CPR' })} className={buttonClass('team_callout')} aria-label="Closed-loop verbal call-out to the team">Closed-loop callout</button>
              <button onClick={() => send('declare_differential', { text: 'unknown' })} className={buttonClass('declare_differential')} aria-label="Declare a working differential diagnosis">Declare differential</button>
            </div>
          </div>

          {/* 4Hs / 4Ts considered chips — each is individually clickable. A real team verbalises
              each cause and decides if it's been considered/excluded. The "Declare all reviewed"
              shortcut exists for speed but the deliberate per-chip click is the teaching point. */}
          <div className="panel p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-mono-white/60 text-xs uppercase tracking-widest">Reversible causes considered (4Hs / 4Ts)</div>
              <button
                onClick={() => send('declare_4hs_4ts', { considered: FOUR_HS_4TS.map((c) => c.key) })}
                className="text-mono-white/50 text-xs hover:text-mono-white underline"
                title="Mark all 8 as considered (quick action — but deliberate per-cause is better practice)"
              >
                Declare all reviewed
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {FOUR_HS_4TS.map((c) => {
                const isOn = considered.has(c.key);
                return (
                  <button
                    key={c.key}
                    onClick={() => send('declare_4hs_4ts', { considered: [c.key] })}
                    disabled={isOn}
                    className={`${isOn ? 'chip-on' : 'chip-off'} hover:brightness-125 transition-all ${isOn ? 'opacity-100 cursor-default' : 'cursor-pointer hover:bg-ink-900'}`}
                    aria-pressed={isOn}
                  >
                    {isOn ? '✓ ' : ''}{c.label}
                  </button>
                );
              })}
            </div>
            {considered.size === 0 && (
              <p className="text-mono-white/40 text-xs mt-3">Tap a chip to mark that cause as considered. Verbalise each one to your team as you go.</p>
            )}
            {considered.size > 0 && considered.size < 8 && (
              <p className="text-mono-white/40 text-xs mt-3">{considered.size} / 8 considered. The unconsidered ones are easy to miss in a real arrest — work through them deliberately.</p>
            )}
          </div>
        </div>

        {/* Right: monitor + action feed + teach feed */}
        <div className="lg:col-span-2 space-y-4">
          <MonitorPanel state={{ ...state, shock_at_ms: lastShockAt }} />
          {/* NEWS2 obs-chart for pre-arrest scenarios — auto-calculated from current vitals.
              Visible only in the pre-arrest phase; once the patient arrests, the NEWS2 is moot. */}
          {currentPhase === 'pre_arrest' && state.hr > 0 && (
            <NEWS2Panel
              hr={state.hr}
              sbp={state.sbp}
              spo2={state.spo2}
              rr={state.rr || 14}
              gcs={(state as any).gcs ?? 15}
            />
          )}
          {/* Screen-reader live narration. Visually hidden; renders an aria-live="polite"
              region that re-announces vitals every 10s, immediately on rhythm change, and
              on demand via the N key. Visible to sighted users via Settings → Calm mode (TODO). */}
          <MonitorAsText state={{ ...state, shock_at_ms: lastShockAt }} />

          <div className="panel p-3">
            <div className="text-mono-white/60 text-xs uppercase tracking-widest mb-2">Action log</div>
            <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {actionLog.length === 0 && (
                <p className="text-mono-white/40 text-xs">No actions yet. Click a button or press Space (CPR), D (shock), A (adrenaline).</p>
              )}
              {actionLog.map((a) => (
                <div key={a.id} className="flex gap-2 text-xs">
                  <span className="text-mono-white/40 font-mono w-14 tabular-nums">{new Date(a.t).toLocaleTimeString(undefined, { hour12: false })}</span>
                  <span className="text-mono-white">{a.label}</span>
                </div>
              ))}
            </div>
          </div>

          {teachFeed.length > 0 && (
            <div className="panel p-3">
              <div className="text-mono-white/60 text-xs uppercase tracking-widest mb-2">Teach feed</div>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {teachFeed.slice().reverse().map((t) => (
                  <div key={t.id} className={`text-xs p-2 rounded ${
                    t.severity === 'critical' ? 'bg-alarm-red/10 text-alarm-red ring-1 ring-alarm-red/40' :
                    t.severity === 'warn' ? 'bg-alarm-amber/10 text-alarm-amber ring-1 ring-alarm-amber/40' :
                    'bg-brand-600/10 text-mono-cyan ring-1 ring-brand-600/30'
                  }`}>
                    <div className="text-mono-white/40 font-mono mb-0.5">T+{(t.t_ms / 1000).toFixed(0)}s</div>
                    <div className="whitespace-pre-wrap">{t.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
